import "./ShareDialog.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DialogProps } from "@mui/material";
import classNames from "classnames";
import {
  COLLECTION_ID,
  DELETE_FIRESTORE_FIELD,
  PASSWORD_TYPE,
  ROLE,
  Roles,
  Space,
  SUBSCRIPTION_TIER,
} from "@highnote/server/src/core/entities";
import { ErrorBoundary } from "App/common/ErrorBoundary";
import {
  ShareDialogContext,
  useShareDialogContext,
} from "./ShareDialogContext";
import { ShareDialogMembers } from "./Members";
import { useToast } from "App/common/useToast";
import { highnote } from "@highnote/server/src/sdk";
import {
  getFullRoles,
  hasRole,
  isInviteId,
  getEntityShareKeys,
  MAX_ACTIVE_SPACES_ERROR,
  PERMISSION,
} from "@highnote/server/src/core/shared-util";
import { useAuth } from "../Auth";
import { useConfirmation } from "App/common/useConfirmation";
import {
  LIMIT_TYPE,
  usePlanLimitsContext,
} from "App/common/PlanLimits/usePlanLimits";
import { ShareDialogLink } from "./Link";
import {
  useGlobalSpaceCached,
  useGlobalSpaces,
} from "App/store/spaces/useGlobalSpaces";
import { useChildEntityWatchers } from "App/store/helpers/useChildEntityWatchers";
import { useSpaceContext } from "App/common/useSpace";
import {
  ValidMobileWebAppComponents,
  useMobileAppParams,
} from "App/routes/Main/useMobileAppParams";
import { FullyConfiguredDialogV2 } from "App/common/DialogV2";
import { TabbedViewV2 } from "App/core/TabbedViewV2";
import { useMembershipFlags } from "App/components/MembershipFlagsContext";
import { useMyMemberships } from "App/components/Auth/MyMembershipsContext";
import { reportShareDialogUnresolved } from "App/modules/membershipTelemetry";

enum SHARE_DIALOG_TAB {
  LINK = "link",
  MEMBERS = "members",
  PUBLIC = "public-page",
}

const ShareDialog = ({ open, onClose, className, ...rest }: DialogProps) => {
  const { showShareDialog, validMobileWebAppComponent } = useMobileAppParams();
  const { entity } = useShareDialogContext();
  const [activeTabId, setActiveTabId] = useState<SHARE_DIALOG_TAB>(() => {
    if (showShareDialog) {
      return validMobileWebAppComponent ===
        ValidMobileWebAppComponents.ShareDialogLink
        ? SHARE_DIALOG_TAB.LINK
        : SHARE_DIALOG_TAB.MEMBERS;
    }

    return SHARE_DIALOG_TAB.LINK;
  });

  const tabs = useMemo(() => {
    return [
      {
        id: SHARE_DIALOG_TAB.LINK,
        name: "Link",
        view: <ShareDialogLink />,
        onClick: () => setActiveTabId(SHARE_DIALOG_TAB.LINK),
      },
      {
        id: SHARE_DIALOG_TAB.MEMBERS,
        name: "Members",
        view: <ShareDialogMembers />,
        onClick: () => setActiveTabId(SHARE_DIALOG_TAB.MEMBERS),
      },
      // We removed the "Public Page" option in this PR: https://github.com/highnotefm/highnote/pull/835
    ];
  }, [entity]);

  return (
    <ErrorBoundary name="ShareDialog">
      <FullyConfiguredDialogV2
        open={open}
        className={classNames("ShareDialog", className)}
        PaperProps={{
          className: classNames("ShareDialog__paper", {
            paperFullScreen: showShareDialog,
          }),
        }}
        header={{
          superTitle: entity?.name,
          title: "Share",
          hideHeader: showShareDialog,
          removeBottomBorder: true,
        }}
        contentProps={{
          removePadding: true,
        }}
        onClose={onClose}
        fullScreen={showShareDialog}
        {...rest}
      >
        <TabbedViewV2
          activeTabId={activeTabId || SHARE_DIALOG_TAB.MEMBERS}
          tabs={tabs}
          hideTabBar={showShareDialog}
          ariaLabel="Share dialog tabs"
          tabsProps={{
            variant: "fullWidth",
          }}
        />
      </FullyConfiguredDialogV2>
    </ErrorBoundary>
  );
};

const ContextualShareDialog = ({
  open,
  onClose,
  space,
}: DialogProps & { space: Space }) => {
  const { user, isAllowed } = useAuth();
  const { toasted, addErrorMessage } = useToast();

  const { globalSpaces } = useGlobalSpaces();
  const { manageGlobalChildEntityWatchers } = useChildEntityWatchers();
  const childSpaces = useMemo(
    () => globalSpaces.childSpaces.get(space?.id),
    [globalSpaces.childSpaces.get(space?.id), space?.id],
  );
  const { confirm, renderConfirmation } = useConfirmation();
  const { showPlanLimitsDialog } = usePlanLimitsContext();
  const { resolveMember } = useMyMemberships();
  const { membersV2Enabled } = useMembershipFlags();
  const ownerMember =
    membersV2Enabled && user?.id && space?.id
      ? resolveMember(space.id, user.id)
      : undefined;

  useEffect(() => {
    if (space?.id) {
      manageGlobalChildEntityWatchers({
        // SPACE_TRACK is needed alongside TRACK: ShareDialogMembers reads
        // `getChildTracks` (via useChildEntityWatchers) to build
        // `recommendedGuests` from each child track's `createdBy` — without
        // the row watcher, a space holding only borrowed tracks would never
        // recommend the guests who added them.
        collectionIdsToWatch: [
          COLLECTION_ID.SPACE,
          COLLECTION_ID.TRACK,
          COLLECTION_ID.SPACE_TRACK,
        ],
        spaceId: space.id,
        componentId: "share-dialog",
      }).attach();
    }

    return () => {
      // Avoid closing watcher if it was started elsewhere (ie. existingSpaceWatcher)
      if (space?.id) {
        manageGlobalChildEntityWatchers({
          collectionIdsToWatch: [
            COLLECTION_ID.SPACE,
            COLLECTION_ID.TRACK,
            COLLECTION_ID.SPACE_TRACK,
          ],
          spaceId: space.id,
          componentId: "share-dialog",
        }).detach();
      }
    };
  }, [space?.id]);

  const updateEntityPassword = async ({
    passwordType,
    password,
    enabled,
  }: {
    passwordType: PASSWORD_TYPE;
    password?: string;
    enabled?: boolean;
  }) => {
    await highnote.updateSpace({
      id: space.id,
      data: {
        ...(typeof enabled === "boolean" && {
          [passwordType === PASSWORD_TYPE.Share
            ? "sharePasswordEnabled"
            : "publicPasswordEnabled"]: enabled,
        }),
        ...(typeof password === "string" && {
          [passwordType === PASSWORD_TYPE.Share
            ? "sharePassword"
            : "publicPassword"]: password,
        }),
      },
    });
  };

  const updateEntityRoles = async (
    roles: Roles,
    shouldThrowError?: boolean,
  ) => {
    let hasInvites = false;
    const oldRoles = getFullRoles(space);
    const newRolesAsUpdateData: Roles = {};

    Object.entries(roles).forEach(([userId, userRoles]) => {
      if (isInviteId(userId) && !!userRoles && !oldRoles[userId]) {
        hasInvites = true;
      }

      newRolesAsUpdateData[`rolesV2,${userId}`] =
        userRoles || (DELETE_FIRESTORE_FIELD as unknown as ROLE[]);
    });

    const newEntity = {
      ...space,
      rolesV2: { ...(space.rolesV2 || {}), ...roles },
    };

    if (
      hasRole(user?.id, ROLE.MANAGE, space) &&
      !hasRole(user?.id, ROLE.MANAGE, newEntity)
    ) {
      const canView = hasRole(user?.id, ROLE.VIEW, newEntity);
      await confirm({
        title: "Remove Access",
        body: `By proceeding with this change, you will no longer have${
          canView ? " management" : ""
        } access to this space. Are you sure you want to continue?`,
      });
    }

    try {
      await toasted({
        promise: highnote.updateSpace({
          id: space.id,
          data: newRolesAsUpdateData,
        }),
        ...(hasInvites
          ? {
              successMessage: "Invites sent!",
            }
          : {}),
      });
    } catch (e) {
      if (e.message === MAX_ACTIVE_SPACES_ERROR) {
        showPlanLimitsDialog(LIMIT_TYPE.SPACES);
        if (shouldThrowError) {
          throw e;
        }
        return;
      }

      addErrorMessage(
        `Could not ${
          hasInvites ? "invite users" : "update permissions"
        }. Please try again.`,
      );
      if (shouldThrowError) {
        throw e;
      }
    }
  };

  const canManageEntity = isAllowed(PERMISSION.TO_MANAGE_SPACE, {
    space,
  });
  const canManageDownloadControl = isAllowed(PERMISSION.TO_DISABLE_DOWNLOAD, {
    space,
  });

  // password protection can be enabled only for spaces, nothing else,
  // and the space owner has to be on a paid plan
  const entityShareKeys = space ? getEntityShareKeys(space) : [];
  const canManagePassword =
    entityShareKeys.length > 0 &&
    space?.subscriptionTier &&
    space.subscriptionTier !== SUBSCRIPTION_TIER.FREE;

  const value = useMemo(
    () => ({
      updateEntityRoles,
      updateEntityPassword,
      entityHasChildren: childSpaces?.length > 0,
      entity: space,
      /*
       * Prefer the stored fact. The `createdBy` comparison is correct when
       * the client has the space document, but it is not the contract. When
       * the app later supports ownership transfer, the comparison no longer
       * matches the server. `isCreator` is a required stored field because
       * the creator override is rule 4 in the steps that compute a member's
       * effective roles. Rule 4 discards what rules 1-3 produce. When
       * there is no member document, fall back to the comparison. That is
       * the state every row is in when the flag is off.
       *
       * Warning: do not use `isCreator` for the roster's "Owner" tag. That
       * tag stays on `effectiveRoles.includes(ADMIN)`. A non-creator with a
       * literal `[ADMIN]` direct grant renders "Owner" today, and a switch
       * to `isCreator` silently relabels exactly those users. See
       * `ShareDialog/Members/index.tsx`.
       */
      isEntityOwner: ownerMember
        ? ownerMember.isCreator === true
        : !!user && !!space && user.id === space.createdBy,
      defaultFeatureAlertMessage: `Contact the space owner.`,
      canManageEntity,
      canManageDownloadControl,
      canManagePassword,
    }),
    [
      user,
      updateEntityRoles,
      updateEntityPassword,
      childSpaces,
      space,
      ownerMember,
      canManageEntity,
      canManagePassword,
      canManageDownloadControl,
    ],
  );

  return (
    <ShareDialogContext.Provider value={value}>
      <ShareDialog open={open} onClose={onClose} />
      {renderConfirmation}
    </ShareDialogContext.Provider>
  );
};

export const useShareDialog = () => {
  const { showShareDialog, redirectToMobileApp } = useMobileAppParams();
  const { getGlobalSpace } = useGlobalSpaces();
  const { getChildSpaces } = useChildEntityWatchers();
  const { space, spaceId } = useSpaceContext();

  const [entity, setEntity] = useState<Space>();

  // this assumes that a given space for the share dialog will always be available
  // in the global state.
  const shareEntity = useMemo(() => {
    if (!entity) {
      return null;
    }
    if (spaceId) {
      if (entity.id && spaceId === entity.id) {
        return space;
      }
      const childSpaces = getChildSpaces(spaceId);
      const targetChildSpace = childSpaces.find(
        (childSpace) => childSpace.id === entity.id,
      );
      if (targetChildSpace) {
        return targetChildSpace;
      }
    }

    return getGlobalSpace(entity.id) || getGlobalSpace(entity.spaceId);
  }, [entity, space, spaceId, getChildSpaces, getGlobalSpace]);

  /*
   * The trap this block repairs: a Share dialog that renders from a
   * `SpaceStub` and quietly does the wrong thing.
   *
   * The dialog needs the full space document: `rolesV2`, `inheritedRolesV3`,
   * `subscriptionTier`, `visibility`, and `updatedAt`. Under the new read
   * model the sidebar and Library lists hold stubs, and `getGlobalSpace`'s
   * fallback searches legacy documents only. So a dialog opened on a space
   * whose document this client never fetched resolves to nothing: a Share
   * button that does nothing. The other outcome is worse. A dialog that
   * renders from a stub looks correct, shows an empty roster, reads every
   * toggle as off, and writes `rolesV2,<user>` while `getFullRoles(stub)`
   * returns `{}`, so the warning that stops you from removing your own
   * access never appears.
   *
   * This block repairs the first outcome. It fetches the document for as
   * long as the dialog needs it, and the dialog stays shut until the
   * document arrives.
   *
   * `"share-dialog"` is already a legal `WatcherComponentId`, and
   * `ContextualShareDialog` attaches child watchers under the same id. So
   * both requests share one reference count, and the detach stays correct
   * without a new id.
   *
   * `shouldFetchSpace` keeps the legacy path byte-identical, and each of its
   * three terms earns its place. `entity` means the dialog has something to
   * show. `!shareEntity` means the resolver above found nothing, which is
   * the only case that needs a fetch. The read flag means rows can exist at
   * all: `shareEntity` can be empty today too, and that is the pre-existing
   * Share button that does nothing. Without the flag term, this fetch would
   * make that button start working, which is a change to the app's sharing
   * surface that must not ride along inside a data migration.
   */
  const { membersV2Enabled } = useMembershipFlags();

  const shouldFetchSpace = Boolean(entity && !shareEntity && membersV2Enabled);
  const { space: fetchedSpace } = useGlobalSpaceCached({
    id: entity?.id,
    componentId: "share-dialog",
    enabled: shouldFetchSpace,
  });
  const resolvedSpace = shareEntity || fetchedSpace;

  useEffect(() => {
    if (!entity || resolvedSpace || !shouldFetchSpace) return;
    const timer = window.setTimeout(() => {
      /*
       * This report fires only when the document never arrived. It separates
       * "the Share button is slow" from "the Share button is broken". Today
       * there is no signal for either.
       */
      reportShareDialogUnresolved(entity.id);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [entity, resolvedSpace, shouldFetchSpace]);

  const openShareDialog = useCallback((_entity: Space) => {
    setEntity(_entity);
  }, []);

  const closeShareDialog = useCallback(() => {
    if (showShareDialog) {
      // redirect to the native deep link via the browser
      redirectToMobileApp();
      return;
    }

    setEntity(undefined);
  }, [showShareDialog]);

  const renderShareDialog = useMemo(() => {
    const baseComponent = (
      <ContextualShareDialog
        /* The existing `Boolean(entity && …)` shape of `open` is intact, so
         * the dialog opens when, and only when, it has a real document. */
        open={Boolean(entity && resolvedSpace)}
        onClose={closeShareDialog}
        space={resolvedSpace}
      />
    );
    return baseComponent;
  }, [entity, resolvedSpace, closeShareDialog]);

  return {
    openShareDialog,
    renderShareDialog: <>{renderShareDialog}</>,
  };
};
